import { useState } from "react";
import { toast } from "sonner";
import { Package, Check, X, Infinity as InfinityIcon } from "lucide-react";
import { updateFavorite } from "../lib/api";

/**
 * Per-region inventory editor. Empty input = unlimited.
 * 0 = sold out (visible OOS state on the storefront).
 */
export default function RegionStockEditor({ favorite, onUpdated, rank, regions }) {
  const [editing, setEditing] = useState(false);
  const [stock, setStock] = useState(() => {
    const rs = favorite.region_stock || {};
    const out = {};
    for (const r of regions) {
      out[r.code] = rs[r.code] !== undefined && rs[r.code] !== null ? String(rs[r.code]) : "";
    }
    return out;
  });
  const [saving, setSaving] = useState(false);

  const setQty = (code, val) => setStock((s) => ({ ...s, [code]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const region_stock = {};
      for (const [code, v] of Object.entries(stock)) {
        if (v === "" || v === null || v === undefined) continue;
        const num = parseInt(v, 10);
        if (!Number.isNaN(num) && num >= 0) {
          region_stock[code] = num;
        }
      }
      const updated = await updateFavorite(favorite.id, { region_stock });
      toast.success("Inventory saved");
      setEditing(false);
      onUpdated?.(updated);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const trackedCount = Object.values(favorite.region_stock || {}).filter(
    (v) => v !== null && v !== undefined,
  ).length;

  if (!editing) {
    return (
      <div className="mt-3 pt-3 border-t border-zinc-100" data-testid={`region-stock-${rank}`}>
        <button
          onClick={() => setEditing(true)}
          data-testid={`open-region-stock-${rank}`}
          className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1"
        >
          <Package size={10} strokeWidth={2.5} />
          {trackedCount > 0
            ? `Inventory · ${trackedCount} region${trackedCount > 1 ? "s" : ""} tracked`
            : "Set inventory per region"}
        </button>
        {trackedCount > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {regions.map((r) => {
              const v = (favorite.region_stock || {})[r.code];
              if (v === null || v === undefined) return null;
              const isOOS = v === 0;
              const isLow = v > 0 && v <= 3;
              return (
                <span
                  key={r.code}
                  className={`text-[9px] font-mono px-2 py-0.5 border ${
                    isOOS
                      ? "bg-red-50 border-red-200 text-red-700"
                      : isLow
                      ? "bg-amber-50 border-amber-200 text-amber-700"
                      : "bg-zinc-100 border-zinc-200 text-zinc-700"
                  }`}
                >
                  {r.code}: {isOOS ? "SOLD OUT" : `${v} units`}
                </span>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="mt-3 p-3 border border-zinc-900 bg-zinc-50"
      data-testid={`region-stock-open-${rank}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Package size={12} strokeWidth={2.5} />
        <span className="micro-label text-[9px]">Inventory Per Region</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {regions.map((r) => (
          <div key={r.code}>
            <label className="micro-label block mb-1 text-[9px]">{r.code}</label>
            <div className="flex items-center border border-zinc-300 bg-white">
              <input
                type="number"
                min="0"
                step="1"
                value={stock[r.code]}
                onChange={(e) => setQty(r.code, e.target.value)}
                placeholder="∞"
                data-testid={`region-stock-input-${r.code}-${rank}`}
                className="w-full px-2 py-1.5 font-mono text-xs focus:outline-none"
              />
              <span className="px-2 text-zinc-400 text-[9px] uppercase tracking-wider">units</span>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[9px] text-zinc-500 mb-3 leading-relaxed flex items-center gap-1">
        <InfinityIcon size={10} strokeWidth={2} /> Empty = unlimited · 0 = sold out
      </p>
      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid={`save-region-stock-${rank}`}
          className="flex-1 bg-[#002FA7] text-white text-[10px] uppercase tracking-widest font-bold py-2 px-3 inline-flex items-center justify-center gap-1 hover:bg-[#001f70] disabled:opacity-50"
        >
          <Check size={12} strokeWidth={3} /> SAVE
        </button>
        <button
          onClick={() => setEditing(false)}
          data-testid={`cancel-region-stock-${rank}`}
          className="px-3 border border-zinc-900 text-[10px] uppercase tracking-widest font-bold hover:bg-zinc-100"
        >
          <X size={12} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}
